import { useEffect } from 'react';

import { IntlProvider, LocalizationProvider } from '@progress/kendo-react-intl';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { useUserDetails, useUsers } from 'pages/users/context';
import { UserProfile } from 'pages/users/components';
import { useNotification } from 'app/notifications';
import { PageHeader } from 'components/layout/page';
import { KendoIntlProvider } from 'app/culture';
import { helpContent } from 'pages/users/help';
import { useParams } from 'react-router-dom';
import { UsersApi } from 'apis/UsersApi';
import { useSuperState } from 'hooks';
import { useIntl } from 'react-intl';

import 'styles/global-styles.scss';

export const UserDetail = () => {

    const { id } = useParams();
    const userId = Number(id);

    const [state, setState] = useSuperState({
        loading: true
    });

    const { open, pageContext } = useUserDetails();
    const { currentPartnerId, permissions } = useUsers();
    const { formatMessage, locale } = useIntl();
    const { catchWithToast, createToast } = useNotification();

    const loadUser = async () => {

        try {

            setState({ loading: true });

            const allUsers = await UsersApi.GetAll(1, currentPartnerId);
            const user = allUsers.find(u => u.UserProfileID === userId);

            if (!user) {

                createToast(formatMessage({ id: 'missing_id_on_user_username.a657acac', defaultMessage: 'Missing ID on user {username}' }, { username: String(userId) }), 'danger');
                setState({ loading: false });
                return;

            }

            const [userPermissions, earlyAccessData] = await Promise.all([
                UsersApi.GetPermissions(user.Id.toString(), currentPartnerId).catch(catchWithToast(formatMessage({ id: 'failed_to_load_user_permissions.aa77e10f', defaultMessage: 'Failed to load user permissions.' }))),
                UsersApi.GetEarlyAccessPreference(user.Id).catch(catchWithToast(formatMessage({ id: 'failed_to_load_early_access_preferences.e3e7bd85', defaultMessage: 'Failed to load early access preferences.' })))
            ]);

            if (earlyAccessData) user.EarlyAccessPreference = earlyAccessData;
            user.CurrentPartnerId = currentPartnerId;

            open({
                Data: {
                    userDetails: { ...user, Permissions: userPermissions },
                    userDetailsOriginal: { ...user, Permissions: userPermissions }
                },
                HasChanges: false,
                Id: user.UserProfileID,
                Title: `${user.FirstName} ${user.LastName}`
            });

        } catch (e) {

            console.error(e);
            createToast(formatMessage({ id: 'failed_to_load_user_details_please_try_a.e702b3c0', defaultMessage: 'Failed to load user details. Please try again.' }), 'danger');

        } finally {

            setState({ loading: false });

        }

    };

    useEffect(() => {

        loadUser();

    }, [userId]);

    return (
        <KendoIntlProvider>
            <LocalizationProvider language={locale}>
                <IntlProvider locale={locale}>
                    <div className="user-detail d-flex flex-column gap-3">
                        <PageHeader
                            helpContent={helpContent}
                            title={pageContext?.Title ?? ''}
                            trail={[{ label: formatMessage({ id: 'administrators.018ac745', defaultMessage: 'Administrators', description: 'menu' }), to: '/administrators' }]}
                        />

                        {state.loading || !pageContext ?
                            (
                                <div className="d-flex align-items-center justify-content-center">
                                    <GMetrixLoader />
                                </div>
                            )
                            :
                            (
                                <UserProfile
                                    currentUserIndex={0}
                                    getAllUsers={() => undefined}
                                    permissions={permissions}
                                    setUsers={() => undefined}
                                    user={pageContext.Data.userDetails}
                                    users={[pageContext.Data.userDetails]}
                                />
                            )
                        }
                    </div>
                </IntlProvider>
            </LocalizationProvider>
        </KendoIntlProvider>
    );

};
